import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';

const Register = () => {
  const [form, setForm] = useState({ student_number: '', name: '', email: '', password: '', confirm: '', role: 'student' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (form.password !== form.confirm) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    const result = await register({ student_number: form.student_number, name: form.name, email: form.email || undefined, password: form.password, role: form.role });
    if (result.success) {
      navigate(result.user.role === 'teacher' ? '/teacher/dashboard' : '/student/quiz');
    } else {
      setError(result.error || 'Error al registrar');
    }
    setLoading(false);
  };

  const inputClass = 'w-full h-10 px-3 bg-sunken border border-line rounded-lg text-sm text-ink placeholder:text-faint focus:outline-none focus:border-accent focus:bg-surface focus:shadow-focus transition-all';

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,var(--accent-soft),transparent)] pointer-events-none" />
      <div className="relative w-full max-w-sm animate-fade-in py-8">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center mb-4 shadow-md">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Crear cuenta</h1>
          <p className="text-sm text-muted mt-1">Únete a Quiz App</p>
        </div>

        <div className="bg-surface border border-line rounded-xl p-6 shadow-sm">
          {error && (
            <div className="mb-4 px-3 py-2.5 rounded-lg bg-danger-soft text-danger-ink text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-body">Número de estudiante *</label>
              <input type="text" value={form.student_number} onChange={set('student_number')} placeholder="ej. 202012345" required className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-body">Nombre</label>
              <input type="text" value={form.name} onChange={set('name')} placeholder="Tu nombre completo" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-body">Correo electrónico</label>
              <input type="email" value={form.email} onChange={set('email')} placeholder="tu@email.com" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-body">Contraseña *</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} placeholder="Mínimo 6 caracteres" required className={`${inputClass} pr-10`} />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-faint hover:text-body transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-body">Confirmar contraseña *</label>
              <input type="password" value={form.confirm} onChange={set('confirm')} placeholder="Repite tu contraseña" required className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-body">Rol</label>
              <select value={form.role} onChange={set('role')} className={`${inputClass} cursor-pointer`}>
                <option value="student">Estudiante</option>
                <option value="teacher">Docente</option>
              </select>
            </div>

            <button type="submit" disabled={loading}
              className="w-full h-10 bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2 mt-1">
              {loading ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Creando cuenta…</> : <>Crear cuenta <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-muted">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-accent-ink hover:text-accent font-medium transition-colors">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
